import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Хранилище черновика, переживающее запрет данных сайта.
 *
 * Обращение к `sessionStorage` бросает `SecurityError`, когда браузеру запрещено хранить данные
 * сайта (настройка Chrome, «блокировать все cookie» в Safari, корпоративная политика). Тогда
 * `createJSONStorage` возвращает `undefined`, а zustand в этом случае **не создаёт `api.persist`
 * вовсе** — и любой вызов `usePostFormStore.persist.clearStorage()` роняет форму заявки в error
 * boundary: создать LFG становится нельзя совсем. Поэтому подставляем хранилище в памяти:
 * черновик живёт до перезагрузки вкладки, а не сохраняется — но форма работает.
 */
const memoryStorage = (): Storage => {
  const data = new Map<string, string>();

  return {
    get length() {
      return data.size;
    },
    clear: () => data.clear(),
    getItem: (key: string) => data.get(key) ?? null,
    key: (index: number) => [...data.keys()][index] ?? null,
    removeItem: (key: string) => void data.delete(key),
    setItem: (key: string, value: string) => void data.set(key, value),
  };
};

let fallbackStorage: Storage | null = null;

const safeSessionStorage = (): Storage => {
  try {
    // Обращение к глобали, а не к `window.sessionStorage`: на сервере и под стабом в тестах
    // окна нет. Одного чтения мало — часть браузеров пускает к объекту и падает на записи.
    const probe = '__tp_probe__';
    sessionStorage.setItem(probe, probe);
    sessionStorage.removeItem(probe);
    return sessionStorage;
  } catch {
    fallbackStorage ??= memoryStorage();
    return fallbackStorage;
  }
};
import { immer } from 'zustand/middleware/immer';

const POST_FORM_STORAGE_KEY = 'post-form-storage';

/**
 * Черновик кладём в копию, а не сохраняем то, что пришло: `immer` замораживает всё, что
 * попало в состояние, а сюда приходят ЖИВЫЕ значения react-hook-form (`watch` отдаёт
 * поверхностную копию, вложенный `gameSettings` — тот самый объект, который форма потом
 * меняет на месте). Без копии первый же выбор настройки после записи черновика падал бы
 * с «object is not extensible».
 */
const detach = (formData: Record<string, unknown>): Record<string, unknown> => {
  try {
    return structuredClone(formData);
  } catch {
    // Внутри оказалось что-то неклонируемое — пишем как есть: потерять черновик хуже,
    // чем заморозить край объекта.
    return { ...formData };
  }
};

type PostFormStore = {
  formData: Record<string, unknown>;
  /**
   * Шаг мастера. Лежит рядом с черновиком, потому что восстанавливать их нужно вместе:
   * возврат с привязки OAuth перемонтирует форму, и без шага человек попадает на первый
   * экран поверх уже заполненных данных.
   */
  step: number;
  setFormData: (formData: Record<string, unknown>) => void;
  setStep: (step: number) => void;
  reset: () => void;
};

export const usePostFormStore = create<PostFormStore>()(
  persist(
    immer((set) => ({
      formData: {},
      step: 0,
      setFormData: (formData) => {
        set((state) => {
          state.formData = {
            ...state.formData,
            ...detach(formData),
          };
        });
      },
      setStep: (step) => {
        set((state) => {
          state.step = step;
        });
      },
      reset: () => {
        set(() => ({
          formData: {},
          step: 0,
        }));
      },
    })),
    {
      name: POST_FORM_STORAGE_KEY,
      // sessionStorage, а не localStorage: черновик нужен ровно этой вкладке и ровно до
      // её закрытия. На localStorage две вкладки с формой затирали черновик друг другу
      // (вторая при монтировании чистит хранилище), а после выхода из аккаунта текст
      // заявки оставался лежать на общем компьютере.
      storage: createJSONStorage(() => safeSessionStorage()),
    }
  )
);

/**
 * Выбросить черновик заявки. Нужен выходу из аккаунта: текст и настройки заявки не должны
 * достаться следующему, кто войдёт в этой вкладке.
 */
export const clearPostFormDraft = () => {
  try {
    usePostFormStore.persist.clearStorage();
  } catch {
    // Хранилище закрыто — чистить нечего.
  }

  usePostFormStore.getState().reset();
};
